import { DataStream } from "@/core/exif/data-stream"
import { TagType } from "@/core/exif/tag-types"
import { getDirectorySize, IFDEntry, writeDirectory, writeTiffHeader } from "@/core/exif/ifd"
import { TagCode } from "@/core/exif/tag-codes"
import { displayP3, sRGB } from "@/core/exif/icc-profiles"
import { PixelData, RGBPlanesDataLayout } from "@/core/utils/image-data"

type ScratchBufferProvider = (byteLength: number) => Uint8Array

export interface TiffScratchBufferOptions {
	width: number
	height: number
	bitDepth: 8 | 16
	rowsPerStrip?: number
	targetStripByteLength?: number
}

export interface TiffEncodedByteLengthOptions extends TiffScratchBufferOptions {
	ppi?: number
	colorSpace?: PredefinedColorSpace
}

export interface TiffEncodeOptions {
	ppi?: number
	rowsPerStrip?: number
	targetStripByteLength?: number
	scratchBufferProvider?: ScratchBufferProvider
}

export interface EncodedTIFFData {
	type: "image/tiff"
	size: number
	writeTo(
		writable: Pick<FileSystemWritableFileStream, "write">,
		options?: { signal?: AbortSignal },
	): Promise<void>
	toBlob(): Promise<Blob>
}

export interface TiffEncoderLike {
	encodeTIFF(pixelData: PixelData, options?: TiffEncodeOptions): Promise<Blob>
	encodeTIFFData?(pixelData: PixelData, options?: TiffEncodeOptions): Promise<EncodedTIFFData>
}

const DEFAULT_TARGET_STRIP_BYTE_LENGTH = 8 * 1024 * 1024
const TIFF_MIME_TYPE = "image/tiff"

export function getTIFFBufferByteLengths(options: TiffScratchBufferOptions) {
	if (options.width <= 0 || options.height <= 0) {
		return { pixelByteLength: 0, scratchByteLength: 0 }
	}

	const rowByteLength = getTIFFRowByteLength(options.width, options.bitDepth)
	const rowsPerStrip = getTIFFRowsPerStrip(options, rowByteLength)

	return {
		pixelByteLength: options.height * rowByteLength,
		scratchByteLength: Math.min(rowsPerStrip, options.height) * rowByteLength,
	}
}

export function getTIFFEncodedByteLength(options: TiffEncodedByteLengthOptions) {
	if (options.width <= 0 || options.height <= 0) {
		return 0
	}

	const samplesPerPixel = 3
	const rowByteLength = getTIFFRowByteLength(options.width, options.bitDepth)
	const rowsPerStrip = getTIFFRowsPerStrip(options, rowByteLength)
	const stripCount = Math.ceil(options.height / rowsPerStrip)
	const stripOffsets = new Array<number>(stripCount)
	const stripByteCounts = new Array<number>(stripCount)
	const bitsPerSample = new Array(samplesPerPixel).fill(options.bitDepth)
	let totalImageBytes = 0

	for (let stripIndex = 0; stripIndex < stripCount; stripIndex++) {
		const startRow = stripIndex * rowsPerStrip
		const stripRows = Math.min(rowsPerStrip, options.height - startRow)
		const stripByteLength = stripRows * rowByteLength
		stripByteCounts[stripIndex] = stripByteLength
		totalImageBytes += stripByteLength
	}

	const entries: IFDEntry[] = [
		{ tag: TagCode.NewSubFileType, type: TagType.LONG, value: 0 },
		{ tag: TagCode.ImageWidth, type: TagType.LONG, value: options.width },
		{ tag: TagCode.ImageLength, type: TagType.LONG, value: options.height },
		{ tag: TagCode.BitsPerSample, type: TagType.SHORT, value: bitsPerSample },
		{ tag: TagCode.Compression, type: TagType.SHORT, value: 1 },
		{ tag: TagCode.PhotometricInterpretation, type: TagType.SHORT, value: 2 },
		{ tag: TagCode.StripOffsets, type: TagType.LONG, value: stripOffsets },
		{ tag: TagCode.SamplesPerPixel, type: TagType.SHORT, value: samplesPerPixel },
		{ tag: TagCode.RowsPerStrip, type: TagType.LONG, value: rowsPerStrip },
		{ tag: TagCode.StripByteCounts, type: TagType.LONG, value: stripByteCounts },
		{ tag: TagCode.XResolution, type: TagType.RATIONAL, value: options.ppi ?? 300 },
		{ tag: TagCode.YResolution, type: TagType.RATIONAL, value: options.ppi ?? 300 },
		{ tag: TagCode.PlanarConfiguration, type: TagType.SHORT, value: 1 },
		{ tag: TagCode.ResolutionUnit, type: TagType.SHORT, value: 2 },
	]

	const iccProfile = options.colorSpace ? getTIFFICCProfile(options.colorSpace) : null
	if (iccProfile) {
		entries.push({ tag: TagCode.ICCProfile, type: TagType.UNDEFINED, value: iccProfile })
	}

	return 8 + getDirectorySize(entries) + totalImageBytes
}

export async function encodeTIFF(
	pixelData: PixelData,
	options: TiffEncodeOptions = {},
): Promise<Blob> {
	return await encodeTIFFData(pixelData, options).toBlob()
}

export function encodeTIFFData(
	pixelData: PixelData,
	options: TiffEncodeOptions = {},
): EncodedTIFFData {
	const { data, width, height, colorSpace } = pixelData

	if (data instanceof Float32Array) {
		throw new Error("TIFF format does not support float")
	}

	const bitDepth = data instanceof Uint16Array ? 16 : 8
	const bytesPerSample = bitDepth >> 3
	const rgbPlanesDataLayout = pixelData.rgbPlanesDataLayout

	if (!rgbPlanesDataLayout) {
		throw new Error("TIFF export requires RGB plane data")
	}

	validateRGBPlanesLayout(data, rgbPlanesDataLayout, width, height, bytesPerSample)

	const samplesPerPixel = 3
	const rowByteLength = getTIFFRowByteLength(width, bitDepth)
	const imageByteLength = rowByteLength * height

	if (imageByteLength > 0xffffffff) {
		throw new Error("TIFF export exceeds the 4 GB classic TIFF limit; BigTIFF is required")
	}

	const rowsPerStrip = getTIFFRowsPerStrip({ ...options, width, height, bitDepth }, rowByteLength)
	const stripCount = Math.ceil(height / rowsPerStrip)
	const stripOffsets = new Array<number>(stripCount)
	const stripByteCounts = new Array<number>(stripCount)
	const bitsPerSample = new Array(samplesPerPixel).fill(bitDepth)
	let totalImageBytes = 0
	let scratchByteLength = 0

	for (let stripIndex = 0; stripIndex < stripCount; stripIndex++) {
		const startRow = stripIndex * rowsPerStrip
		const stripRows = Math.min(rowsPerStrip, height - startRow)
		const stripByteLength = stripRows * rowByteLength
		stripByteCounts[stripIndex] = stripByteLength
		totalImageBytes += stripByteLength
		scratchByteLength = Math.max(scratchByteLength, stripByteLength)
	}

	const entries: IFDEntry[] = [
		{ tag: TagCode.NewSubFileType, type: TagType.LONG, value: 0 },
		{ tag: TagCode.ImageWidth, type: TagType.LONG, value: width },
		{ tag: TagCode.ImageLength, type: TagType.LONG, value: height },
		{ tag: TagCode.BitsPerSample, type: TagType.SHORT, value: bitsPerSample },
		{ tag: TagCode.Compression, type: TagType.SHORT, value: 1 },
		{ tag: TagCode.PhotometricInterpretation, type: TagType.SHORT, value: 2 },
		{ tag: TagCode.StripOffsets, type: TagType.LONG, value: stripOffsets },
		{ tag: TagCode.SamplesPerPixel, type: TagType.SHORT, value: samplesPerPixel },
		{ tag: TagCode.RowsPerStrip, type: TagType.LONG, value: rowsPerStrip },
		{ tag: TagCode.StripByteCounts, type: TagType.LONG, value: stripByteCounts },
		{ tag: TagCode.XResolution, type: TagType.RATIONAL, value: options.ppi ?? 300 },
		{ tag: TagCode.YResolution, type: TagType.RATIONAL, value: options.ppi ?? 300 },
		{ tag: TagCode.PlanarConfiguration, type: TagType.SHORT, value: 1 },
		{ tag: TagCode.ResolutionUnit, type: TagType.SHORT, value: 2 },
	]

	const iccProfile = getTIFFICCProfile(colorSpace)
	if (iccProfile) {
		entries.push({ tag: TagCode.ICCProfile, type: TagType.UNDEFINED, value: iccProfile })
	}

	const headerByteLength = 8 + getDirectorySize(entries)
	const totalByteLength = headerByteLength + totalImageBytes

	if (totalByteLength > 0xffffffff) {
		throw new Error("TIFF export exceeds the 4 GB classic TIFF limit; BigTIFF is required")
	}

	let nextStripOffset = headerByteLength
	for (let stripIndex = 0; stripIndex < stripCount; stripIndex++) {
		stripOffsets[stripIndex] = nextStripOffset
		nextStripOffset += stripByteCounts[stripIndex] ?? 0
	}

	const header = new ArrayBuffer(headerByteLength)
	const dataView = new DataView(header)
	const dataStream = new DataStream(dataView)

	writeTiffHeader(dataStream)
	writeDirectory(dataStream, entries)

	const encoded = new ChunkedTIFFData({
		header: new Uint8Array(header),
		pixelData: { data, rgbPlanesDataLayout, width, height },
		rowByteLength,
		rowsPerStrip,
		stripByteCounts,
		size: totalByteLength,
		scratchBufferProvider:
			options.scratchBufferProvider ?? createFixedScratchBufferProvider(scratchByteLength),
	})

	return encoded
}

function getTIFFICCProfile(colorSpace: PredefinedColorSpace) {
	switch (colorSpace) {
		case "display-p3":
			return displayP3
		case "srgb":
			return sRGB
	}
}

function getTIFFRowByteLength(width: number, bitDepth: 8 | 16) {
	return width * 3 * (bitDepth >> 3)
}

function getTIFFRowsPerStrip(options: TiffScratchBufferOptions, rowByteLength: number) {
	if (options.rowsPerStrip != null) {
		return Math.max(1, Math.floor(options.rowsPerStrip))
	}

	if (rowByteLength <= 0) {
		return 1
	}

	return Math.max(
		1,
		Math.floor(
			(options.targetStripByteLength ?? DEFAULT_TARGET_STRIP_BYTE_LENGTH) / rowByteLength,
		),
	)
}

function createFixedScratchBufferProvider(byteLength: number) {
	const scratchView = new Uint8Array(new ArrayBuffer(byteLength))

	return (requestedByteLength: number) => {
		if (requestedByteLength > scratchView.byteLength) {
			throw new Error("TIFF scratch buffer is smaller than the requested strip")
		}

		return scratchView.subarray(0, requestedByteLength)
	}
}

class ChunkedTIFFData implements EncodedTIFFData {
	public readonly type = TIFF_MIME_TYPE
	public readonly size: number

	private readonly header: Uint8Array
	private readonly pixelData: {
		data: Uint8Array | Uint16Array
		rgbPlanesDataLayout: RGBPlanesDataLayout
		width: number
		height: number
	}
	private readonly rowByteLength: number
	private readonly rowsPerStrip: number
	private readonly stripByteCounts: number[]
	private readonly scratchBufferProvider: ScratchBufferProvider

	constructor(options: {
		header: Uint8Array
		pixelData: {
			data: Uint8Array | Uint16Array
			rgbPlanesDataLayout: RGBPlanesDataLayout
			width: number
			height: number
		}
		rowByteLength: number
		rowsPerStrip: number
		stripByteCounts: number[]
		size: number
		scratchBufferProvider: ScratchBufferProvider
	}) {
		this.header = options.header
		this.pixelData = options.pixelData
		this.rowByteLength = options.rowByteLength
		this.rowsPerStrip = options.rowsPerStrip
		this.stripByteCounts = options.stripByteCounts
		this.size = options.size
		this.scratchBufferProvider = options.scratchBufferProvider
	}

	public async writeTo(
		writable: Pick<FileSystemWritableFileStream, "write">,
		options?: { signal?: AbortSignal },
	): Promise<void> {
		options?.signal?.throwIfAborted()
		await writable.write(asFileSystemWriteChunk(this.header))

		for (let stripIndex = 0; stripIndex < this.stripByteCounts.length; stripIndex++) {
			options?.signal?.throwIfAborted()
			const chunk = this.getStripChunk(stripIndex, false)
			await writable.write(asFileSystemWriteChunk(chunk))
		}
	}

	public toBlob(): Promise<Blob> {
		const parts: BlobPart[] = [this.header.slice()]

		for (let stripIndex = 0; stripIndex < this.stripByteCounts.length; stripIndex++) {
			parts.push(this.getStripChunk(stripIndex, true) as unknown as BlobPart)
		}

		return Promise.resolve(new Blob(parts, { type: TIFF_MIME_TYPE }))
	}

	private getStripChunk(stripIndex: number, copy: boolean): Uint8Array {
		const startRow = stripIndex * this.rowsPerStrip
		const rows = Math.min(this.rowsPerStrip, this.pixelData.height - startRow)
		const byteLength = this.stripByteCounts[stripIndex] ?? rows * this.rowByteLength
		const chunk = packRGBPlanesChunk(
			this.pixelData.data,
			this.pixelData.rgbPlanesDataLayout,
			this.pixelData.width,
			startRow,
			rows,
			this.scratchBufferProvider(byteLength),
		)

		return copy ? chunk.slice() : chunk
	}
}

function validateRGBPlanesLayout(
	data: Uint8Array | Uint16Array,
	layout: RGBPlanesDataLayout,
	width: number,
	height: number,
	bytesPerSample: number,
) {
	const planeByteLength = width * height * bytesPerSample
	const planes = [
		{ offset: layout.rOffset, length: layout.rLength },
		{ offset: layout.gOffset, length: layout.gLength },
		{ offset: layout.bOffset, length: layout.bLength },
	]

	for (const plane of planes) {
		if (plane.offset % bytesPerSample !== 0) {
			throw new Error("RGB plane TIFF export has misaligned plane offsets")
		}

		if (plane.length < planeByteLength) {
			throw new Error("RGB plane TIFF export has a plane smaller than the image size")
		}

		if (plane.offset + plane.length > data.byteLength) {
			throw new Error("RGB plane TIFF export layout exceeds the data buffer")
		}
	}
}

function asFileSystemWriteChunk(data: Uint8Array): FileSystemWriteChunkType {
	return data as Uint8Array<ArrayBuffer>
}

function packRGBPlanesChunk(
	data: Uint8Array | Uint16Array,
	layout: RGBPlanesDataLayout,
	width: number,
	startRow: number,
	rows: number,
	output: Uint8Array,
): Uint8Array {
	if (data instanceof Uint16Array) {
		return packRGB16PlanesChunk(data, layout, width, startRow, rows, output)
	}

	const pixelCount = width * rows
	let rIndex = layout.rOffset + startRow * width
	let gIndex = layout.gOffset + startRow * width
	let bIndex = layout.bOffset + startRow * width
	let targetIndex = 0

	for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex++) {
		output[targetIndex++] = data[rIndex++] ?? 0
		output[targetIndex++] = data[gIndex++] ?? 0
		output[targetIndex++] = data[bIndex++] ?? 0
	}

	return output.subarray(0, pixelCount * 3)
}

function packRGB16PlanesChunk(
	data: Uint16Array,
	layout: RGBPlanesDataLayout,
	width: number,
	startRow: number,
	rows: number,
	output: Uint8Array,
): Uint8Array {
	const pixelCount = width * rows
	const output16 = new Uint16Array(output.buffer, output.byteOffset, pixelCount * 3)
	let rIndex = (layout.rOffset >> 1) + startRow * width
	let gIndex = (layout.gOffset >> 1) + startRow * width
	let bIndex = (layout.bOffset >> 1) + startRow * width
	let targetIndex = 0

	for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex++) {
		output16[targetIndex++] = data[rIndex++] ?? 0
		output16[targetIndex++] = data[gIndex++] ?? 0
		output16[targetIndex++] = data[bIndex++] ?? 0
	}

	return output.subarray(0, pixelCount * 3 * 2)
}
